import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import OngoingSpellProgress from '../admissions/ongoingSpellProgress';
import { IconButton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SingleNoteComponent from './singleNote';
import { clinicalNoteTypeDict } from '../../store/reducers/dicts';
import clsx from 'clsx';
import { noteFilters } from '../../store/reducers/notes';
import AttendanceChip from './component/attendanceChip';
import Chip from '@mui/material/Chip';
import { getConfig } from '../../util/configUtil';
import moment from 'moment';
import { useClinicalNoteStyles } from './useClinicalNoteStyles';
import { hasPermission, Permissions } from '../../store/reducers/user';
import { setSnackbar } from '../../store/reducers/globals';

const AdmissionClinicalNotes = ({
    filterText,
    noteAdmission,
    hideNote,
    editNote,
    disableSave,
    showNoteAudit,
    checkLastOfType,
    markAsError,
    patient,
}) => {
    const classes = useClinicalNoteStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');

    // Selector
    const clinicalNoteType = useSelector(clinicalNoteTypeDict);
    const filter = useSelector(noteFilters);

    // Expand state
    const isFromReviewCtx = noteAdmission?.admission === null;
    const isCanceled = noteAdmission?.admission?.canceled;
    const isActive = !isCanceled && _.isNil(noteAdmission?.admission?.endDate);
    const [expanded, setExpanded] = useState(isActive && !isFromReviewCtx);

    // Permission
    const canEnterCanceled = dispatch(hasPermission(Permissions.AGYLE_CAN_SEE_CANCELED));

    const [notes, setNotes] = useState([]);
    const isED = noteAdmission?.admission?.attendanceType === 'E';

    useEffect(() => {
        setExpanded(filter.expanded || isActive);
    }, [filter.expanded]);

    useEffect(() => {
        const grouped = _.groupBy(noteAdmission?.note, (n) => n['episodeId']);
        let finalObj = [];

        if (!_.isEmpty(noteAdmission?.admission?.episodes)) {
            const ordered = _.orderBy(noteAdmission?.admission?.episodes, ['startDate'], ['desc']);
            _.forEach(ordered, (ep) => {
                if (!ep?.episodeNumber) return;

                finalObj.push({
                    id: ep?.episodeNumber,
                    notes: grouped[ep?.episodeNumber] || [],
                    startDate: ep?.startDate,
                });
            });
        }

        finalObj.push({
            id: 'no_episode_number',
            notes: grouped['null'],
            startDate: null,
        });
        setNotes(finalObj);
    }, [noteAdmission?.note]);

    const isVisible = (note) => {
        if (!expanded) return false;
        if (!filterText) return true;
        if (note?.note) {
            if (note.note.startsWith('data:image/png;base64')) return false;
            return note.note.toLowerCase().includes(filterText.toLowerCase());
        }
        return false;
    };

    const handleExpand = () => {
        if (isCanceled && !canEnterCanceled) {
            dispatch(setSnackbar({ type: 'warning', message: t('You do not have permission to access cancelled activities') }));
            return;
        }
        setExpanded(!expanded);
    };

    const renderMainChip = () => {
        return (
            <div className={clsx(classes.divRow, classes.noteAdmissionRoot)} style={{ gap: 10 }}>
                <AttendanceChip admission={noteAdmission.admission} reviewCtx={isFromReviewCtx} />
                <IconButton size="small" onClick={handleExpand}>
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>

                {!_.isEmpty(noteAdmission.admission?.episodes) && (
                    <Chip label={`${noteAdmission.admission?.episodes?.length || 0} ${t('Ep.')}`} variant="outlined" />
                )}

                {isActive && !isFromReviewCtx && <OngoingSpellProgress />}

                {isCanceled && (
                    <div className={classes.divRow} style={{ width: '100%' }}>
                        <Chip label={t('Activity cancelled')} variant="outlined" style={{ background: '#E25B61', color: '#fff' }} />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            {renderMainChip()}

            {notes.map((note) => {
                if (_.isEmpty(note?.notes) || !expanded) return null;

                let label = `${t(`Inpatient Episode ${moment(note?.startDate).format(dateFormat)}`)}`;
                if (!note?.startDate) label = t('Inpatient other notes');

                return (
                    <Fragment key={note?.id}>
                        {!isED && !isFromReviewCtx && <Chip label={label} variant="outlined" style={{ marginBottom: 20 }} />}

                        {note?.notes?.map((note, index) => {
                            if (!isVisible(note)) return null;

                            return (
                                <SingleNoteComponent
                                    filterText={filterText}
                                    index={index}
                                    onHide={hideNote(note)}
                                    onEdit={editNote(note)}
                                    onAudit={showNoteAudit(note)}
                                    isLastOfType={checkLastOfType(note)}
                                    handleError={() => markAsError(note)}
                                    key={note.__id}
                                    note={note}
                                    disableSave={disableSave}
                                    clinicalNoteTypes={clinicalNoteType}
                                    patient={patient}
                                />
                            );
                        })}
                    </Fragment>
                );
            })}
        </div>
    );
};

export default AdmissionClinicalNotes;
